<template>
  <div class="expenses">
    <header class="section-header">
      <div>
        <h1>Gastos</h1>
        <p class="muted">
          Gestiona gastos puntuales y recurrencias para todas tus secciones.
        </p>
      </div>
      <div class="header-actions">
        <button class="ghost" @click="openRecurringModal()">+ Nueva recurrencia</button>
        <button class="primary" @click="openModal()">+ Nuevo gasto</button>
      </div>
    </header>

    <div class="filters-row">
      <label class="filter">
        Sección
        <select v-model="sectionFilter">
          <option value="">Todas</option>
          <option v-for="option in sectionOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </label>
    </div>

    <section v-if="loading" class="summary-grid skeleton-summary" role="status" aria-live="polite">
      <span class="sr-only">Cargando gastos...</span>
      <div class="card highlight skeleton-card">
        <div class="skeleton-stack">
          <div class="skeleton skeleton-line lg" style="width: 38%"></div>
          <div class="skeleton skeleton-line xl" style="width: 68%"></div>
          <div class="skeleton skeleton-line" style="width: 52%"></div>
        </div>
        <div class="skeleton-metrics">
          <div class="skeleton skeleton-line" style="width: 70%"></div>
          <div class="skeleton skeleton-line" style="width: 58%"></div>
        </div>
      </div>
      <div class="card chart skeleton-chart">
        <div class="skeleton skeleton-circle" style="width: 140px; height: 140px"></div>
        <div class="skeleton-stack">
          <div class="skeleton skeleton-line lg" style="width: 42%"></div>
          <div class="skeleton skeleton-line" style="width: 70%"></div>
          <div class="skeleton skeleton-line" style="width: 64%"></div>
          <div class="skeleton skeleton-line" style="width: 58%"></div>
        </div>
      </div>
    </section>

    <section v-if="loading" class="upcoming" aria-hidden="true">
      <h2>Próximos pagos</h2>
      <div class="upcoming-grid">
        <div v-for="n in 3" :key="`up-skel-${n}`" class="upcoming-card skeleton-card">
          <div class="skeleton skeleton-line lg" style="width: 60%"></div>
          <div class="skeleton skeleton-line" style="width: 44%"></div>
          <div class="skeleton skeleton-line" style="width: 36%"></div>
        </div>
      </div>
    </section>

    <section v-if="loading" class="records" aria-hidden="true">
      <h2>Listado</h2>
      <div class="records-grid">
        <div v-for="n in 3" :key="`rec-skel-${n}`" class="record-card skeleton-card">
          <div class="skeleton skeleton-line lg" style="width: 58%"></div>
          <div class="skeleton skeleton-line" style="width: 42%"></div>
          <div class="skeleton skeleton-line" style="width: 70%"></div>
          <div class="skeleton skeleton-line" style="width: 38%"></div>
        </div>
      </div>
    </section>

    <section v-if="!loading" class="summary-grid">
      <div class="card highlight">
        <div class="summary-header">
          <div>
            <h2>Resumen</h2>
            <p class="muted">Totales del periodo actual</p>
          </div>
        </div>
        <div class="summary-metrics summary-metrics--four">
          <div>
            <p>Mes actual</p>
            <h3>{{ formatEur(currentMonthOccurrences) }}</h3>
            <span class="muted small">{{ currentMonthLabel }}</span>
          </div>
          <div>
            <p>Año actual</p>
            <h3>{{ formatEur(currentYearOccurrences) }}</h3>
            <span class="muted small">{{ currentYearLabel }}</span>
          </div>
          <div>
            <p>Recurrentes mensual</p>
            <h3>{{ formatEur(recurringMonthlyTotal) }}</h3>
            <span class="muted small">Equivalente mensual</span>
          </div>
          <div>
            <p>Recurrentes anual</p>
            <h3>{{ formatEur(recurringYearlyTotal) }}</h3>
            <span class="muted small">Equivalente anual</span>
          </div>
        </div>
      </div>

      <div class="card chart">
        <h2>Gasto por sección</h2>
        <div class="donut-wrap">
          <div class="donut">
            <svg class="donut-svg" viewBox="0 0 120 120" aria-hidden="true">
              <circle class="donut-ring" cx="60" cy="60" r="42"></circle>
              <circle
                v-for="segment in sectionSegments"
                :key="segment.key"
                class="donut-segment"
                cx="60"
                cy="60"
                r="42"
                :stroke="segment.color"
                :stroke-dasharray="segment.dashArray"
                :stroke-dashoffset="segment.dashOffset"
              ></circle>
            </svg>
          </div>
          <div class="donut-legend">
            <div v-for="(item, index) in sectionGroups" :key="item.key" class="legend-item">
              <span class="swatch" :style="{ background: palette[index % palette.length] }"></span>
              <div>
                <strong>{{ item.label }}</strong>
                <p>{{ formatEur(item.total) }}</p>
              </div>
              <span class="legend-pct">{{ formatShare(item.sharePct) }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section v-if="error" class="state error">{{ error }}</section>

    <section v-if="!loading" class="upcoming">
      <h2>Próximos pagos</h2>
      <div class="upcoming-grid">
        <div v-for="item in filteredUpcoming" :key="`up-${item.id}`" class="upcoming-card">
          <div>
            <h3>{{ item.name }}</h3>
            <p class="muted">{{ formatDate(item.nextDueAt || item.startAt) }}</p>
            <p v-if="item.provider?.name" class="muted small">
              {{ item.provider.name }} · {{ sectionLabel(item.section) }}
            </p>
          </div>
          <span class="chip">{{ formatEur(item.amountEur) }}</span>
        </div>
        <div v-if="!filteredUpcoming.length" class="empty-card">
          <p>No hay pagos próximos.</p>
        </div>
      </div>
    </section>

    <section v-if="!loading" class="expenses-timeline" role="region" aria-labelledby="expenses-title">
      <div class="expenses-timeline__header">
        <div>
          <h2 id="expenses-title">Histórico de gastos</h2>
          <p class="muted">Registro cronológico de tus gastos.</p>
        </div>
      </div>
      <div class="expenses-timeline__list">
        <article
          v-for="group in expenseTimelineGroups"
          :key="group.key"
          class="expenses-timeline__group"
        >
          <div class="timeline-month">
            <span class="timeline-dot" aria-hidden="true"></span>
            <span class="timeline-month__label">{{ group.label }}</span>
          </div>
          <div class="timeline-group__items">
            <article
              v-for="item in group.items"
              :key="item.id"
              class="expenses-timeline__item"
            >
              <span class="timeline-dot" aria-hidden="true"></span>
              <button class="timeline-card timeline-card--compact" @click="openModal(item)">
                <div>
                  <h3>{{ item.name }}</h3>
                  <p class="muted">{{ formatDate(item.plannedAt) }}</p>
                </div>
                <span class="chip">{{ formatEur(item.amountEur) }}</span>
              </button>
            </article>
          </div>
        </article>
        <div v-if="!filteredExpenses.length" class="empty-card">
          <p>No hay gastos registrados todavía.</p>
          <button class="ghost" @click="openModal()">Crear el primero</button>
        </div>
      </div>
    </section>

    <section v-if="!loading" class="records">
      <div class="section-title">
        <h2>Recurrencias</h2>
        <button class="ghost" @click="openRecurringModal()">+ Nueva recurrencia</button>
      </div>
      <div class="records-grid">
        <div v-for="item in filteredRecurring" :key="`rec-${item.id}`" class="record-card">
          <div class="record-header">
            <div>
              <h3>{{ item.name }}</h3>
              <p class="muted">Inicio {{ formatDate(item.startAt) }}</p>
              <p v-if="item.provider?.name" class="muted small">
                {{ item.provider.name }} · {{ sectionLabel(item.section) }}
              </p>
            </div>
            <div class="record-meta">
              <span class="chip">{{ formatEur(item.amountEur) }}</span>
              <span class="chip ghost">{{ recurrenceLabel(item) }}</span>
            </div>
          </div>
          <div class="next-due" v-if="item.nextDueAt">
            Próximo: <strong>{{ formatDate(item.nextDueAt) }}</strong>
          </div>
          <div class="next-due" v-if="item.lastOccurrenceAt">
            Último gasto: {{ formatDate(item.lastOccurrenceAt) }}
          </div>
          <div class="record-actions">
            <button class="ghost" @click="openRecurringModal(item)">Editar</button>
            <button class="ghost danger" @click="removeRecurring(item)">Eliminar</button>
          </div>
        </div>
        <div v-if="!filteredRecurring.length" class="empty-card">
          <p>No hay recurrencias todavía.</p>
          <button class="ghost" @click="openRecurringModal()">Crear la primera</button>
        </div>
      </div>
    </section>

    <div v-if="showModal" class="modal-backdrop" @click.self="closeModal">
      <div class="modal modal-lg">
        <header>
          <h3>{{ form.id ? "Editar gasto" : "Nuevo gasto" }}</h3>
          <button class="icon danger" @click="closeModal">×</button>
        </header>
        <div class="modal-body">
          <label>
            Sección
            <select v-model="form.section">
              <option v-for="option in sectionOptions" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </label>

          <label v-if="form.section === 'VEHICLE'">
            Vehículo
            <select v-model="form.vehicleId">
              <option :value="null">Selecciona un vehículo</option>
              <option v-for="vehicle in vehicles" :key="vehicle.id" :value="vehicle.id">
                {{ vehicle.brand }} {{ vehicle.model }} · {{ vehicle.licensePlate }}
              </option>
            </select>
          </label>

          <label v-if="form.section === 'INSURANCE'">
            Seguro
            <select v-model="form.insurancePolicyId">
              <option :value="null">Selecciona un seguro</option>
              <option v-for="policy in insurances" :key="policy.id" :value="policy.id">
                {{ policy.name }}
              </option>
            </select>
          </label>

          <label>
            Nombre
            <input v-model="form.name" type="text" placeholder="Alquiler, seguro, hosting..." />
          </label>
          <label>
            Descripción
            <textarea v-model="form.description" rows="3" placeholder="Detalles opcionales"></textarea>
          </label>
          <div class="row">
            <label>
              Fecha prevista
              <input v-model="form.plannedAt" type="date" />
            </label>
            <label>
              Precio EUR
              <input v-model.number="form.amountEur" type="number" step="0.01" />
            </label>
          </div>

          <label>
            Asociar a recurrencia (opcional)
            <select v-model="form.recurringId">
              <option :value="null">Sin recurrencia</option>
              <option
                v-for="recurring in recurringOptions"
                :key="`link-${recurring.id}`"
                :value="recurring.id"
              >
                {{ recurring.name }} · {{ recurrenceLabel(recurring) }}
              </option>
            </select>
          </label>

          <label>
            Proveedor
            <div class="provider-field">
              <input
                v-model="providerSearch"
                placeholder="Busca o crea un proveedor"
                @input="onProviderInput"
                @focus="openProviderOptions"
                @blur="closeProviderOptions"
              />
              <button class="ghost" @click="createProviderFromInput">Crear</button>
              <div v-if="showProviderOptions" class="provider-options">
                <button
                  v-for="provider in providerSuggestions"
                  :key="provider.id"
                  type="button"
                  class="provider-option"
                  @mousedown.prevent="selectProvider(provider)"
                >
                  {{ provider.name }}
                </button>
                <div v-if="!providerSuggestions.length" class="provider-empty">
                  Sin coincidencias
                </div>
              </div>
            </div>
          </label>

          <label>
            Tipología
            <div class="provider-field">
              <input
                v-model="categorySearch"
                placeholder="Busca o crea una tipología"
                @input="onCategoryInput"
                @focus="openCategoryOptions"
                @blur="closeCategoryOptions"
              />
              <button class="ghost" @click="createCategoryFromInput">Crear</button>
              <div v-if="showCategoryOptions" class="provider-options">
                <button
                  v-for="category in categorySuggestions"
                  :key="category.id"
                  type="button"
                  class="provider-option"
                  @mousedown.prevent="selectCategory(category)"
                >
                  {{ category.name }}
                </button>
                <div v-if="!categorySuggestions.length" class="provider-empty">
                  Sin coincidencias
                </div>
              </div>
            </div>
          </label>

          <div class="row">
            <label>
              Email proveedor
              <input v-model="providerForm.email" type="email" placeholder="correo@proveedor.com" />
            </label>
            <label>
              Teléfono proveedor
              <input v-model="providerForm.phone" type="text" placeholder="+34 600 000 000" />
            </label>
          </div>
          <label>
            Dirección proveedor
            <input v-model="providerForm.address" type="text" placeholder="Calle / ciudad / país" />
          </label>
          <label>
            Información adicional
            <textarea
              v-model="providerForm.additionalInfo"
              rows="2"
              placeholder="Notas internas, condiciones, recordatorios..."
            ></textarea>
          </label>

          <div class="tickets">
            <div class="ticket-header">
              <strong>Tickets</strong>
              <button class="ghost" type="button" @click="openTicketFileDialog">Añadir archivos</button>
            </div>
            <input
              ref="modalFiles"
              type="file"
              class="sr-only"
              multiple
              accept="image/*,application/pdf"
              @change="onModalFilesSelected"
            />
            <p class="muted small">Soporta imágenes o PDF.</p>

            <div v-if="existingTickets.length" class="ticket-list">
              <div v-for="ticket in existingTickets" :key="`existing-${ticket.id}`" class="ticket-item">
                <span>{{ ticket.name || "Ticket" }}</span>
                <button class="ghost danger" type="button" @click="removeExistingTicket(ticket.id)">
                  Eliminar
                </button>
              </div>
            </div>

            <div v-if="selectedTicketFiles.length" class="ticket-list">
              <div
                v-for="(ticket, index) in selectedTicketFiles"
                :key="ticket.id"
                class="ticket-item"
              >
                <span>{{ ticket.name }}</span>
                <button class="ghost danger" type="button" @click="removePendingTicket(index)">
                  Quitar
                </button>
              </div>
            </div>
          </div>
        </div>
        <footer>
          <button class="ghost" @click="closeModal">Cancelar</button>
          <button class="primary" @click="saveExpense">Guardar</button>
        </footer>
      </div>
    </div>

    <div v-if="showRecurringModal" class="modal-backdrop" @click.self="closeRecurringModal">
      <div class="modal">
        <header>
          <h3>{{ recurringForm.id ? "Editar recurrencia" : "Nueva recurrencia" }}</h3>
          <button class="icon danger" @click="closeRecurringModal">×</button>
        </header>
        <div class="modal-body">
          <label>
            Sección
            <select v-model="recurringForm.section">
              <option v-for="option in sectionOptions" :key="`rec-${option.value}`" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </label>

          <label v-if="recurringForm.section === 'VEHICLE'">
            Vehículo
            <select v-model="recurringForm.vehicleId">
              <option :value="null">Selecciona un vehículo</option>
              <option v-for="vehicle in vehicles" :key="`rec-veh-${vehicle.id}`" :value="vehicle.id">
                {{ vehicle.brand }} {{ vehicle.model }} · {{ vehicle.licensePlate }}
              </option>
            </select>
          </label>

          <label v-if="recurringForm.section === 'INSURANCE'">
            Seguro
            <select v-model="recurringForm.insurancePolicyId">
              <option :value="null">Selecciona un seguro</option>
              <option v-for="policy in insurances" :key="`rec-ins-${policy.id}`" :value="policy.id">
                {{ policy.name }}
              </option>
            </select>
          </label>

          <label>
            Nombre
            <input v-model="recurringForm.name" type="text" placeholder="Suscripción, préstamo..." />
          </label>
          <label>
            Descripción
            <textarea v-model="recurringForm.description" rows="3" placeholder="Detalles opcionales"></textarea>
          </label>
          <div class="row">
            <label>
              Inicio
              <input v-model="recurringForm.startAt" type="date" />
            </label>
            <label>
              Precio EUR
              <input v-model.number="recurringForm.amountEur" type="number" step="0.01" />
            </label>
          </div>
          <div class="row">
            <label>
              Proveedor
              <select v-model="recurringForm.providerId">
                <option :value="null">Sin proveedor</option>
                <option v-for="provider in providers" :key="`rec-prov-${provider.id}`" :value="provider.id">
                  {{ provider.name }}
                </option>
              </select>
            </label>
            <label>
              Tipología
              <select v-model="recurringForm.categoryId">
                <option :value="null">Sin tipología</option>
                <option v-for="category in categories" :key="`rec-cat-${category.id}`" :value="category.id">
                  {{ category.name }}
                </option>
              </select>
            </label>
          </div>
          <div class="row">
            <label>
              Cada
              <input v-model.number="recurringForm.recurrenceValue" type="number" min="1" />
            </label>
            <label>
              Unidad
              <select v-model="recurringForm.recurrenceUnit">
                <option value="DAYS">Días</option>
                <option value="WEEKS">Semanas</option>
                <option value="MONTHS">Meses</option>
                <option value="YEARS">Años</option>
              </select>
            </label>
          </div>
          <label class="checkbox line">
            <input type="checkbox" v-model="recurringForm.active" />
            <span></span>
            Activa
          </label>
        </div>
        <footer>
          <button class="ghost" @click="closeRecurringModal">Cancelar</button>
          <button class="primary" @click="saveRecurring">Guardar</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchExpenses,
  fetchUpcomingExpenses,
  createExpense,
  updateExpense,
  deleteExpense,
  fetchExpenseProviders,
  createExpenseProvider,
  fetchExpenseCategories,
  createExpenseCategory,
  fetchRecurringExpenses,
  createRecurringExpense,
  updateRecurringExpense,
  deleteRecurringExpense,
  deleteExpenseTicket,
} from "../services/expenses";
import { fetchVehicles } from "../services/vehicles";
import { fetchInsurances } from "../services/insurances";

export default {
  name: "ExpensesView",
  data() {
    return {
      expenses: [],
      recurring: [],
      upcoming: [],
      providers: [],
      categories: [],
      vehicles: [],
      insurances: [],
      palette: ["#38bdf8", "#f59e0b", "#22c55e", "#e879f9", "#60a5fa", "#fb7185"],
      loading: false,
      error: "",
      showModal: false,
      showRecurringModal: false,
      form: this.emptyForm(),
      recurringForm: this.emptyRecurringForm(),
      providerSearch: "",
      categorySearch: "",
      showProviderOptions: false,
      showCategoryOptions: false,
      providerForm: { email: "", phone: "", address: "", additionalInfo: "" },
      sectionFilter: "",
      existingTickets: [],
      selectedTicketFiles: [],
    };
  },
  async mounted() {
    await this.loadAll();
  },
  watch: {
    sectionFilter() {
      this.loadAll();
    },
  },
  computed: {
    sectionOptions() {
      return [
        { value: "GENERAL", label: "General" },
        { value: "HOME", label: "Suministros" },
        { value: "VEHICLE", label: "Vehículos" },
        { value: "INSURANCE", label: "Seguros" },
        { value: "HEALTH", label: "Salud" },
        { value: "TAX", label: "Impuestos" },
        { value: "PERSONAL", label: "Personal" },
        { value: "OTHER", label: "Otros" },
      ];
    },
    providerSuggestions() {
      const query = this.providerSearch.trim().toLowerCase();
      const list = this.providers || [];
      if (!query) return list.slice(0, 6);
      return list
        .filter((provider) => provider.name.toLowerCase().includes(query))
        .slice(0, 6);
    },
    categorySuggestions() {
      const query = this.categorySearch.trim().toLowerCase();
      const list = this.categories || [];
      if (!query) return list.slice(0, 6);
      return list
        .filter((category) => category.name.toLowerCase().includes(query))
        .slice(0, 6);
    },
    currentMonthLabel() {
      return new Intl.DateTimeFormat("es-ES", { month: "long", year: "numeric" }).format(
        new Date()
      );
    },
    currentYearLabel() {
      return new Date().getFullYear().toString();
    },
    filteredExpenses() {
      if (!this.sectionFilter) return this.expenses;
      return this.expenses.filter((item) => item.section === this.sectionFilter);
    },
    expenseTimelineGroups() {
      const groups = new Map();
      const items = Array.isArray(this.filteredExpenses) ? this.filteredExpenses : [];
      items.forEach((item) => {
        const date = new Date(item.plannedAt);
        if (Number.isNaN(date.getTime())) return;
        const year = date.getFullYear();
        const month = date.getMonth();
        const key = `${year}-${String(month + 1).padStart(2, "0")}`;
        if (!groups.has(key)) {
          const label = date.toLocaleDateString("es-ES", { month: "short", year: "numeric" });
          groups.set(key, { key, label, items: [] });
        }
        groups.get(key).items.push(item);
      });
      return Array.from(groups.values());
    },
    filteredRecurring() {
      if (!this.sectionFilter) return this.recurring;
      return this.recurring.filter((item) => item.section === this.sectionFilter);
    },
    filteredUpcoming() {
      if (!this.sectionFilter) return this.upcoming;
      return this.upcoming.filter((item) => item.section === this.sectionFilter);
    },
    recurringOptions() {
      const section = this.form.section;
      return this.recurring.filter((item) => item.section === section && item.active !== false);
    },
    currentMonthOccurrences() {
      const now = new Date();
      const month = now.getMonth();
      const year = now.getFullYear();
      return this.filteredExpenses.reduce((sum, item) => {
        const date = new Date(item.plannedAt);
        if (date.getFullYear() === year && date.getMonth() === month) {
          return sum + Number(item.amountEur || 0);
        }
        return sum;
      }, 0);
    },
    currentYearOccurrences() {
      const now = new Date();
      const year = now.getFullYear();
      return this.filteredExpenses.reduce((sum, item) => {
        const date = new Date(item.plannedAt);
        if (date.getFullYear() === year) {
          return sum + Number(item.amountEur || 0);
        }
        return sum;
      }, 0);
    },
    recurringYearlyTotal() {
      return this.filteredRecurring.reduce((sum, item) => {
        const amount = Number(item.amountEur || 0);
        const value = Number(item.recurrenceValue || 1);
        if (!value) return sum;
        if (item.recurrenceUnit === "YEARS") {
          return sum + amount / value;
        }
        if (item.recurrenceUnit === "MONTHS") {
          return sum + amount * (12 / value);
        }
        if (item.recurrenceUnit === "WEEKS") {
          return sum + amount * (52 / value);
        }
        if (item.recurrenceUnit === "DAYS") {
          return sum + amount * (365 / value);
        }
        return sum;
      }, 0);
    },
    recurringMonthlyTotal() {
      const yearly = this.recurringYearlyTotal;
      return yearly / 12;
    },
    sectionGroups() {
      const map = {};
      const now = new Date();
      const currentYear = now.getFullYear();
      const addValue = (section, amount) => {
        const label = this.sectionLabel(section);
        if (!map[label]) {
          map[label] = { key: label, label, total: 0 };
        }
        map[label].total += amount;
      };

      this.filteredExpenses.forEach((item) => {
        const date = new Date(item.plannedAt);
        if (Number.isNaN(date.getTime())) return;
        if (date.getFullYear() !== currentYear) return;
        addValue(item.section, Number(item.amountEur || 0));
      });

      this.filteredRecurring.forEach((item) => {
        const amount = Number(item.amountEur || 0);
        const value = Number(item.recurrenceValue || 1);
        if (!value) return;
        let yearly = 0;
        if (item.recurrenceUnit === "YEARS") {
          yearly = amount / value;
        } else if (item.recurrenceUnit === "MONTHS") {
          yearly = amount * (12 / value);
        } else if (item.recurrenceUnit === "WEEKS") {
          yearly = amount * (52 / value);
        } else if (item.recurrenceUnit === "DAYS") {
          yearly = amount * (365 / value);
        } else {
          yearly = amount;
        }
        addValue(item.section, yearly);
      });
      const groups = Object.values(map);
      groups.sort((a, b) => b.total - a.total);
      const totalValue = groups.reduce((sum, group) => sum + group.total, 0);
      groups.forEach((group) => {
        group.sharePct = totalValue > 0 ? (group.total / totalValue) * 100 : null;
      });
      return groups;
    },
    sectionSegments() {
      if (!this.sectionGroups.length) return [];
      const total = this.sectionGroups.reduce((sum, group) => sum + Number(group.sharePct || 0), 0);
      if (!total) return [];
      const radius = 42;
      const circumference = 2 * Math.PI * radius;
      let cumulative = 0;
      return this.sectionGroups.map((group, index) => {
        const pct = Number(group.sharePct || 0) / 100;
        const segment = {
          key: group.key,
          label: group.label,
          pct: pct * 100,
          color: this.palette[index % this.palette.length],
          dashArray: `${pct * circumference} ${circumference}`,
          dashOffset: -circumference * cumulative,
        };
        cumulative += pct;
        return segment;
      });
    },
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        section: "GENERAL",
        name: "",
        description: "",
        plannedAt: "",
        amountEur: 0,
        currency: "EUR",
        providerId: null,
        categoryId: null,
        recurringId: null,
        vehicleId: null,
        insurancePolicyId: null,
      };
    },
    emptyRecurringForm() {
      return {
        id: null,
        section: "GENERAL",
        name: "",
        description: "",
        startAt: "",
        amountEur: 0,
        currency: "EUR",
        recurrenceValue: 1,
        recurrenceUnit: "MONTHS",
        active: true,
        providerId: null,
        categoryId: null,
        vehicleId: null,
        insurancePolicyId: null,
      };
    },
    async loadAll() {
      this.loading = true;
      this.error = "";
      try {
        const params = this.sectionFilter ? { section: this.sectionFilter } : {};
        const [expenses, upcoming, recurring, providers, categories, vehicles, insurances] =
          await Promise.all([
            fetchExpenses(params),
            fetchUpcomingExpenses(params),
            fetchRecurringExpenses(params),
            fetchExpenseProviders(),
            fetchExpenseCategories(),
            fetchVehicles(),
            fetchInsurances(),
          ]);
        this.expenses = (expenses || []).map((item) => this.normalizeExpense(item));
        this.upcoming = (upcoming || []).map((item) => this.normalizeRecurring(item));
        this.recurring = (recurring || []).map((item) => this.normalizeRecurring(item));
        this.providers = providers || [];
        this.categories = categories || [];
        this.vehicles = vehicles || [];
        this.insurances = insurances || [];
      } catch (err) {
        this.error = err?.message || "No se pudieron cargar los gastos.";
      } finally {
        this.loading = false;
      }
    },
    openProviderOptions() {
      this.showProviderOptions = true;
    },
    closeProviderOptions() {
      setTimeout(() => {
        this.showProviderOptions = false;
      }, 120);
    },
    selectProvider(provider) {
      this.providerSearch = provider.name;
      this.form.providerId = provider.id;
      this.showProviderOptions = false;
    },
    openCategoryOptions() {
      this.showCategoryOptions = true;
    },
    closeCategoryOptions() {
      setTimeout(() => {
        this.showCategoryOptions = false;
      }, 120);
    },
    selectCategory(category) {
      this.categorySearch = category.name;
      this.form.categoryId = category.id;
      this.showCategoryOptions = false;
    },
    openModal(expense) {
      if (expense) {
        const normalized = this.normalizeExpense(expense);
        this.form = {
          id: normalized.id,
          section: normalized.section || "GENERAL",
          name: normalized.name || "",
          description: normalized.description || "",
          plannedAt: normalized.plannedAt,
          amountEur: Number(normalized.amountEur || 0),
          currency: normalized.currency || "EUR",
          providerId: normalized.provider?.id || null,
          categoryId: normalized.category?.id || null,
          recurringId: normalized.recurringId || null,
          vehicleId: normalized.vehicleId || null,
          insurancePolicyId: normalized.insurancePolicyId || null,
        };
        this.providerSearch = normalized.provider?.name || "";
        this.categorySearch = normalized.category?.name || "";
        this.providerForm = {
          email: normalized.provider?.email || "",
          phone: normalized.provider?.phone || "",
          address: normalized.provider?.address || "",
          additionalInfo: normalized.provider?.additionalInfo || "",
        };
        this.existingTickets = Array.isArray(normalized.tickets) ? [...normalized.tickets] : [];
      } else {
        this.form = this.emptyForm();
        this.providerSearch = "";
        this.categorySearch = "";
        this.providerForm = { email: "", phone: "", address: "", additionalInfo: "" };
        this.existingTickets = [];
      }
      this.selectedTicketFiles = [];
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.selectedTicketFiles = [];
      this.existingTickets = [];
    },
    openRecurringModal(recurring) {
      if (recurring) {
        const normalized = this.normalizeRecurring(recurring);
        this.recurringForm = {
          id: normalized.id,
          section: normalized.section || "GENERAL",
          name: normalized.name || "",
          description: normalized.description || "",
          startAt: normalized.startAt,
          amountEur: Number(normalized.amountEur || 0),
          currency: normalized.currency || "EUR",
          recurrenceValue: normalized.recurrenceValue || 1,
          recurrenceUnit: normalized.recurrenceUnit || "MONTHS",
          active: normalized.active !== false,
          providerId: normalized.provider?.id || null,
          categoryId: normalized.category?.id || null,
          vehicleId: normalized.vehicleId || null,
          insurancePolicyId: normalized.insurancePolicyId || null,
        };
      } else {
        this.recurringForm = this.emptyRecurringForm();
      }
      this.showRecurringModal = true;
    },
    closeRecurringModal() {
      this.showRecurringModal = false;
    },
    async saveExpense() {
      const name = this.form.name.trim();
      const plannedAt = this.normalizeDate(this.form.plannedAt);
      if (!name || !plannedAt) {
        this.error = "Nombre y fecha son obligatorios.";
        return;
      }
      if (this.form.section === "VEHICLE" && !this.form.vehicleId) {
        this.error = "Selecciona un vehículo para este gasto.";
        return;
      }
      if (this.form.section === "INSURANCE" && !this.form.insurancePolicyId) {
        this.error = "Selecciona un seguro para este gasto.";
        return;
      }
      try {
        await this.ensureProviderSelected();
        await this.ensureCategorySelected();
        const tickets = await this.buildTicketPayload();
        const payload = this.payload(plannedAt, tickets);
        if (this.form.id) {
          const updated = await updateExpense(this.form.id, payload);
          const normalized = this.normalizeExpense(updated);
          this.expenses = this.expenses.map((item) => (item.id === normalized.id ? normalized : item));
        } else {
          const created = await createExpense(payload);
          this.expenses = [this.normalizeExpense(created), ...this.expenses];
        }
        this.closeModal();
        try {
          await this.loadAll();
        } catch (_) {
          // ignore refresh errors after successful save
        }
      } catch (err) {
        this.error = err?.message || "No se pudo guardar el gasto.";
      }
    },
    async saveRecurring() {
      const name = this.recurringForm.name.trim();
      const startAt = this.normalizeDate(this.recurringForm.startAt);
      if (!name || !startAt) {
        this.error = "Nombre e inicio son obligatorios.";
        return;
      }
      if (this.recurringForm.section === "VEHICLE" && !this.recurringForm.vehicleId) {
        this.error = "Selecciona un vehículo para esta recurrencia.";
        return;
      }
      if (this.recurringForm.section === "INSURANCE" && !this.recurringForm.insurancePolicyId) {
        this.error = "Selecciona un seguro para esta recurrencia.";
        return;
      }
      try {
        const payload = this.recurringPayload(startAt);
        if (this.recurringForm.id) {
          const updated = await updateRecurringExpense(this.recurringForm.id, payload);
          const normalized = this.normalizeRecurring(updated);
          this.recurring = this.recurring.map((item) => (item.id === normalized.id ? normalized : item));
        } else {
          const created = await createRecurringExpense(payload);
          this.recurring = [this.normalizeRecurring(created), ...this.recurring];
        }
        this.closeRecurringModal();
        try {
          await this.loadAll();
        } catch (_) {
          // ignore refresh errors after successful save
        }
      } catch (err) {
        this.error = err?.message || "No se pudo guardar la recurrencia.";
      }
    },
    payload(plannedAt, tickets) {
      return {
        section: this.form.section,
        name: this.form.name,
        description: this.form.description,
        plannedAt,
        amountEur: this.form.amountEur,
        currency: this.form.currency,
        providerId: this.form.providerId,
        categoryId: this.form.categoryId,
        recurringId: this.form.recurringId,
        vehicleId: this.form.vehicleId,
        insurancePolicyId: this.form.insurancePolicyId,
        tickets,
      };
    },
    recurringPayload(startAt) {
      return {
        section: this.recurringForm.section,
        name: this.recurringForm.name,
        description: this.recurringForm.description,
        startAt,
        amountEur: this.recurringForm.amountEur,
        currency: this.recurringForm.currency,
        recurrenceValue: this.recurringForm.recurrenceValue,
        recurrenceUnit: this.recurringForm.recurrenceUnit,
        active: this.recurringForm.active,
        providerId: this.recurringForm.providerId,
        categoryId: this.recurringForm.categoryId,
        vehicleId: this.recurringForm.vehicleId,
        insurancePolicyId: this.recurringForm.insurancePolicyId,
      };
    },
    async removeExpense(expense) {
      if (!confirm(`¿Eliminar "${expense.name}"?`)) return;
      try {
        await deleteExpense(expense.id);
        this.expenses = this.expenses.filter((item) => item.id !== expense.id);
        this.upcoming = this.upcoming.filter((item) => item.id !== expense.id);
      } catch (err) {
        this.error = err?.message || "No se pudo eliminar el gasto.";
      }
    },
    async removeRecurring(recurring) {
      if (!confirm(`¿Eliminar "${recurring.name}"?`)) return;
      try {
        await deleteRecurringExpense(recurring.id);
        this.recurring = this.recurring.filter((item) => item.id !== recurring.id);
      } catch (err) {
        this.error = err?.message || "No se pudo eliminar la recurrencia.";
      }
    },
    onProviderInput() {
      const match = this.providers.find(
        (provider) => provider.name.toLowerCase() === this.providerSearch.trim().toLowerCase()
      );
      if (match) {
        this.form.providerId = match.id;
        this.providerForm = {
          email: match.email || "",
          phone: match.phone || "",
          address: match.address || "",
          additionalInfo: match.additionalInfo || "",
        };
      } else {
        this.form.providerId = null;
      }
    },
    onCategoryInput() {
      const match = this.categories.find(
        (category) => category.name.toLowerCase() === this.categorySearch.trim().toLowerCase()
      );
      this.form.categoryId = match ? match.id : null;
    },
    async createProviderFromInput() {
      const name = this.providerSearch.trim();
      if (!name) return;
      try {
        const provider = await createExpenseProvider({
          name,
          email: this.providerForm.email,
          phone: this.providerForm.phone,
          address: this.providerForm.address,
          additionalInfo: this.providerForm.additionalInfo,
        });
        if (!this.providers.some((item) => item.id === provider.id)) {
          this.providers.push(provider);
        }
        this.form.providerId = provider.id;
        this.providerSearch = provider.name;
      } catch (err) {
        this.error = err?.message || "No se pudo crear el proveedor.";
      }
    },
    async createCategoryFromInput() {
      const name = this.categorySearch.trim();
      if (!name) return;
      try {
        const category = await createExpenseCategory({ name });
        if (!this.categories.some((item) => item.id === category.id)) {
          this.categories.push(category);
        }
        this.form.categoryId = category.id;
        this.categorySearch = category.name;
      } catch (err) {
        this.error = err?.message || "No se pudo crear la tipología.";
      }
    },
    async ensureProviderSelected() {
      const name = this.providerSearch.trim();
      if (!name) {
        this.form.providerId = null;
        return;
      }
      const match = this.providers.find(
        (provider) => provider.name.toLowerCase() === name.toLowerCase()
      );
      if (match) {
        this.form.providerId = match.id;
        return;
      }
      const provider = await createExpenseProvider({
        name,
        email: this.providerForm.email,
        phone: this.providerForm.phone,
        address: this.providerForm.address,
        additionalInfo: this.providerForm.additionalInfo,
      });
      if (!this.providers.some((item) => item.id === provider.id)) {
        this.providers.push(provider);
      }
      this.form.providerId = provider.id;
      this.providerSearch = provider.name;
    },
    async ensureCategorySelected() {
      const name = this.categorySearch.trim();
      if (!name) {
        this.form.categoryId = null;
        return;
      }
      const match = this.categories.find(
        (category) => category.name.toLowerCase() === name.toLowerCase()
      );
      if (match) {
        this.form.categoryId = match.id;
        return;
      }
      const category = await createExpenseCategory({ name });
      if (!this.categories.some((item) => item.id === category.id)) {
        this.categories.push(category);
      }
      this.form.categoryId = category.id;
      this.categorySearch = category.name;
    },
    formatDate(value) {
      if (!value) return "—";
      return new Intl.DateTimeFormat("es-ES", { dateStyle: "medium" }).format(new Date(value));
    },
    normalizeDate(value) {
      if (!value) return null;
      if (typeof value !== "string") {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return null;
        return date.toISOString().slice(0, 10);
      }
      if (value.includes("T")) {
        return value.slice(0, 10);
      }
      if (value.includes("-")) {
        return value;
      }
      const parts = value.split("/");
      if (parts.length !== 3) return null;
      const [day, month, year] = parts;
      if (!day || !month || !year) return null;
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    },
    normalizeExpense(expense) {
      if (!expense) return expense;
      return {
        ...expense,
        plannedAt: this.normalizeDate(expense.plannedAt ?? expense.planned_at),
        amountEur: expense.amountEur ?? expense.amount_eur,
        recurringId: expense.recurringId ?? expense.recurring_id,
        vehicleId: expense.vehicleId ?? expense.vehicle_id,
        insurancePolicyId: expense.insurancePolicyId ?? expense.insurance_policy_id,
      };
    },
    normalizeRecurring(recurring) {
      if (!recurring) return recurring;
      return {
        ...recurring,
        startAt: this.normalizeDate(recurring.startAt ?? recurring.start_at),
        amountEur: recurring.amountEur ?? recurring.amount_eur,
        recurrenceValue: recurring.recurrenceValue ?? recurring.recurrence_value,
        recurrenceUnit: recurring.recurrenceUnit ?? recurring.recurrence_unit,
        lastOccurrenceAt: this.normalizeDate(recurring.lastOccurrenceAt ?? recurring.last_occurrence_at),
        nextDueAt: this.normalizeDate(recurring.nextDueAt ?? recurring.next_due_at),
        vehicleId: recurring.vehicleId ?? recurring.vehicle_id,
        insurancePolicyId: recurring.insurancePolicyId ?? recurring.insurance_policy_id,
      };
    },
    formatEur(value) {
      if (value == null) return "—";
      return new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
      }).format(Number(value));
    },
    formatShare(value) {
      if (value == null) return "—";
      return `${Number(value).toFixed(2)}%`;
    },
    recurrenceLabel(item) {
      const value = item.recurrenceValue || 1;
      const unit = item.recurrenceUnit || "MONTHS";
      const labels = { DAYS: "días", WEEKS: "semanas", MONTHS: "meses", YEARS: "años" };
      return `Cada ${value} ${labels[unit] || ""}`;
    },
    sectionLabel(section) {
      return (
        this.sectionOptions.find((item) => item.value === section)?.label || "General"
      );
    },
    async buildTicketPayload() {
      if (!this.selectedTicketFiles.length) return null;
      const mapped = await Promise.all(
        this.selectedTicketFiles.map(async (item) => ({
          name: item.name,
          type: this.resolveTicketType(item.file),
          format: "BASE64",
          data: await this.fileToBase64(item.file),
        }))
      );
      return mapped;
    },
    resolveTicketType(file) {
      const type = file?.type || "";
      if (type.startsWith("image/")) return "IMAGE";
      return "FILE";
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    onModalFilesSelected(event) {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;
      this.appendTicketFiles(files);
      event.target.value = "";
    },
    openTicketFileDialog() {
      this.$refs.modalFiles?.click();
    },
    appendTicketFiles(files) {
      const mapped = files.map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
        name: file.name,
        file,
      }));
      this.selectedTicketFiles = [...this.selectedTicketFiles, ...mapped];
    },
    removePendingTicket(index) {
      this.selectedTicketFiles.splice(index, 1);
    },
    async removeExistingTicket(ticketId) {
      if (!this.form.id) return;
      if (!confirm("¿Eliminar este ticket?")) return;
      try {
        await deleteExpenseTicket(this.form.id, ticketId);
        this.existingTickets = this.existingTickets.filter((ticket) => ticket.id !== ticketId);
        await this.loadAll();
      } catch (err) {
        this.error = err?.message || "No se pudo eliminar el ticket.";
      }
    },
  },
};
</script>

<style scoped>
.expenses {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.section-header h1 {
  margin: 0 0 6px;
}

.header-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.filters-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.filter {
  display: grid;
  gap: 6px;
  font-size: 13px;
  color: var(--muted);
}

.filter select {
  height: 42px;
  min-width: 180px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(15, 23, 42, 0.6);
  color: var(--text);
  padding: 0 12px;
  font-size: 13px;
}

.filter select:focus {
  outline: none;
  border-color: rgba(56, 189, 248, 0.6);
  box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.15);
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
}

.card {
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 20px;
  padding: 24px;
  box-shadow: var(--shadow);
}

.card.highlight {
  background: linear-gradient(160deg, rgba(56, 189, 248, 0.16), rgba(15, 23, 42, 0.9));
}

.summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.summary-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
}

.summary-metrics p {
  margin: 0 0 6px;
  color: var(--muted);
  font-size: 13px;
}

.expenses-timeline {
  display: grid;
  gap: 16px;
}

.expenses-timeline__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.expenses-timeline__header h2 {
  margin: 0 0 6px;
}

.expenses-timeline__list {
  display: grid;
  gap: 14px;
  position: relative;
  padding-left: 6px;
}

.expenses-timeline__list::before {
  content: "";
  position: absolute;
  left: 8px;
  top: 6px;
  bottom: 6px;
  width: 2px;
  background: rgba(148, 163, 184, 0.18);
}

.expenses-timeline__group {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 16px;
  position: relative;
}

.timeline-month {
  display: flex;
  align-items: center;
  gap: 10px;
  position: sticky;
  top: 14px;
  align-self: start;
}

.timeline-month__label {
  text-transform: uppercase;
  letter-spacing: 0.18em;
  font-size: 11px;
  color: var(--muted);
}

.timeline-group__items {
  display: grid;
  gap: 12px;
}

.expenses-timeline__item {
  display: grid;
  grid-template-columns: 18px 1fr;
  gap: 12px;
  align-items: start;
}

.expenses-timeline__item .timeline-dot {
  margin-top: 16px;
}

.timeline-month .timeline-dot {
  margin-top: 2px;
}

.timeline-card--compact {
  appearance: none;
  border: 1px solid var(--border);
  background: var(--color-card);
  color: var(--text);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  width: 100%;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.2s ease, transform 0.2s ease;
  padding: 14px 16px;
  border-radius: 18px;
}

.timeline-card--compact h3 {
  margin: 0 0 4px;
  font-size: 16px;
}

.timeline-card--compact p {
  margin: 0;
  font-size: 13px;
}

.timeline-card--compact:hover {
  border-color: rgba(56, 189, 248, 0.55);
  transform: translateY(-1px);
}


.summary-metrics h3 {
  margin: 0;
  font-size: 22px;
}

.chart {
  min-height: 220px;
}

.donut-wrap {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 20px;
  align-items: center;
}

.donut {
  width: 150px;
  height: 150px;
  border-radius: 50%;
  position: relative;
  box-shadow: inset 0 0 0 12px rgba(15, 23, 42, 0.8);
}

.donut-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.donut-ring {
  fill: transparent;
  stroke: rgba(148, 163, 184, 0.2);
  stroke-width: 14;
}

.donut-segment {
  fill: transparent;
  stroke-width: 14;
  stroke-linecap: butt;
  transition: opacity 0.2s ease;
}

.donut-segment:hover {
  opacity: 0.85;
}

.donut-legend {
  display: grid;
  gap: 12px;
}

.legend-item {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  align-items: center;
  background: var(--panel-2);
  border-radius: 12px;
  padding: 10px 12px;
  border: 1px solid var(--border);
}

.legend-item p {
  margin: 2px 0 0;
  color: var(--muted);
  font-size: 12px;
}

.legend-pct {
  font-weight: 600;
  color: var(--text);
}

.swatch {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.upcoming,
.records {
  display: grid;
  gap: 16px;
}

.section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.upcoming-grid,
.records-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

.upcoming-card,
.record-card,
.empty-card {
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 16px;
  box-shadow: var(--shadow);
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.record-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.chip {
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.2);
  color: var(--text);
}

.chip.ghost {
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.2);
}

.desc {
  margin: 10px 0;
  color: var(--muted);
}

.next-due {
  margin-bottom: 10px;
  font-size: 13px;
}

.provider-card {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 12px;
  padding: 10px;
  display: grid;
  gap: 4px;
  font-size: 13px;
}

.record-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.primary {
  background: linear-gradient(120deg, var(--accent), #22c55e);
  color: #071022;
  border: none;
  padding: 10px 16px;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;
}

.ghost {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text);
  padding: 8px 12px;
  border-radius: 999px;
  cursor: pointer;
}

.ghost.danger {
  color: var(--danger);
  border-color: rgba(248, 113, 113, 0.4);
}

.provider-field {
  display: grid;
  position: relative;
  gap: 6px;
}

.provider-options {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  background: var(--panel-2);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 8px;
  display: grid;
  gap: 6px;
}

.provider-option {
  text-align: left;
  padding: 6px 8px;
  border-radius: 8px;
  background: transparent;
  border: 1px solid transparent;
  cursor: pointer;
}

.provider-option:hover {
  border-color: rgba(56, 189, 248, 0.4);
}

.provider-empty {
  font-size: 12px;
  color: var(--muted);
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(6, 10, 18, 0.65);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  z-index: 20;
}

.modal {
  width: min(640px, 92vw);
  max-width: calc(100vw - 32px);
  background: var(--panel);
  border-radius: 20px;
  border: 1px solid var(--border);
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: calc(100vh - 48px);
  overflow: hidden;
}

.modal-lg {
  width: min(760px, 96vw);
}

.modal header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 24px 0;
}

.modal header h3 {
  margin: 0;
}

.modal .icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: rgba(15, 23, 42, 0.6);
  color: var(--text);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}

.modal .icon.danger {
  color: var(--danger);
  border-color: rgba(248, 113, 113, 0.4);
}

.modal-body {
  display: grid;
  gap: 16px;
  padding: 20px 24px 8px;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
}

.modal-body label {
  display: grid;
  gap: 6px;
  font-size: 13px;
  color: var(--muted);
}

.modal-body input,
.modal-body select,
.modal-body textarea {
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: rgba(15, 23, 42, 0.6);
  color: var(--text);
  font-size: 14px;
  outline: none;
}

.modal-body textarea {
  min-height: 90px;
  resize: vertical;
}

.modal-body input:focus,
.modal-body select:focus,
.modal-body textarea:focus {
  border-color: rgba(56, 189, 248, 0.6);
  box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.15);
}

.modal footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 0 24px 24px;
}

.checkbox.line {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--text);
  font-size: 13px;
}

.checkbox.line input {
  width: 18px;
  height: 18px;
  accent-color: var(--accent);
}

.row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
}

.ticket-preview {
  margin: 10px 0;
  font-size: 12px;
  color: var(--muted);
}

.tickets {
  margin-top: 16px;
  display: grid;
  gap: 8px;
}

.ticket-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.ticket-list {
  display: grid;
  gap: 6px;
}

.ticket-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.4);
}

@media (max-width: 720px) {
  .donut-wrap {
    grid-template-columns: 1fr;
  }
}
</style>
